import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterModule } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { first, firstValueFrom, last, Observable } from 'rxjs';
import { Group } from '../../../../models/group.model';
import {
  MembershipStatus,
  MemberType,
} from '../../../../models/membership.status';
import { Prayer, PrayerStatus } from '../../../../models/prayer.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { AuthService } from '../../../../services/auth.service';
import * as groupActions from '../../../../state/actions/group.actions';
import * as prayerActions from '../../../../state/actions/prayer.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as prayerSelector from '../../../../state/selectors/prayer.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnRouteComponent } from '../../../components/buttons/btn-route/btn-route.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { ChipComponent, ChipColor } from '../../../components/inputs/chip/chip.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';

@Component({
  selector: 'app-list-prayer',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    BtnBackComponent,
    BtnRouteComponent,
    BtnFabComponent,
    BtnMoreComponent,
    ChipComponent,
    RouterLink,
    NgxSkeletonLoaderModule,
    MomentPipe,
  ],
  templateUrl: './list-prayer.component.html',
  styleUrls: ['./list-prayer.component.css'],
})
export class ListPrayerComponent implements OnInit {
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  auth: AuthService = inject(AuthService);
  private dialog: MatDialog = inject(MatDialog);

  prayers$: Observable<Prayer[] | null | undefined> = new Observable();

  prayersLoading$: Observable<boolean> = new Observable();
  prayerStatsLoading$: Observable<boolean> = new Observable();
  groupStatsLoading$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');

  group$: Observable<Group | null | undefined> = new Observable();

  totalActivePrayers$: Observable<number> = new Observable();

  myRoles$: Observable<any> = new Observable();

  membershipStatus = MembershipStatus;
  memberType = MemberType;

  // Strongly-typed mapping of PrayerStatus to ChipColor
  chipColors: Record<PrayerStatus, ChipColor> = {
    [PrayerStatus.active]: ChipColor.SUCCESS,
    [PrayerStatus.request]: ChipColor.WARNING,
    [PrayerStatus.closed]: ChipColor.INFO,
    [PrayerStatus.rejected]: ChipColor.DANGER,
    [PrayerStatus.unknown]: ChipColor.PRIMARY,
  };

  constructor(private store: Store<AppState>) {
    if (this.groupId != null) {
      this.prayers$ = this.store.pipe(
        select(prayerSelector.getPrayersByGroupId(this.groupId!)),
      );
      this.group$ = this.store.pipe(
        select(groupSelector.getGroupById(this.groupId!)),
      );

      this.myRoles$ = this.store.select(
        groupSelector.getMyRolesInGroup(this.groupId),
      );

      this.prayersLoading$ = this.store
        .select((state) => state.prayers)
        .pipe(select(prayerSelector.loading));

      this.groupStatsLoading$ = this.store
        .select((state) => state.groups)
        .pipe(select(groupSelector.statsLoading));

      this.prayerStatsLoading$ = this.store
        .select((state) => state.prayers)
        .pipe(select(prayerSelector.prayerStatsLoading));
    }
  }

  async ngOnInit(): Promise<void> {
    const group: Group | null | undefined = await firstValueFrom(
      this.group$.pipe(first()),
    );
    const prayerStatsLoading: boolean = await firstValueFrom(
      this.prayerStatsLoading$.pipe(first()),
    );
    const prayers: Prayer[] | null | undefined = await firstValueFrom(
      this.prayers$.pipe(first()),
    );

    const isAuthenticated: boolean = this.auth.isAuthenticated();

    if (group?.adminMembers == undefined && isAuthenticated) {
      this.store.dispatch(
        groupActions.getGroupStats({ groupId: this.groupId! }),
      );
    }
    if (
      group?.openedPrayers == undefined &&
      !prayerStatsLoading &&
      isAuthenticated
    ) {
      this.store.dispatch(
        prayerActions.getPrayerStats({ groupId: this.groupId! }),
      );
    }

    if (prayers?.length == 0 && isAuthenticated) {
      this.store.dispatch(
        prayerActions.getPrayers({
          groupId: this.groupId!,
          memberType:
            (group?.memberType as MemberType | undefined) ?? MemberType.member,
        }),
      );
    }
  }

  onEdit(item: Prayer) {
    this.router.navigate([
      'groups',
      item.groupId,
      'prayers',
      'edit',
      item.id,
    ]);
  }

  async dialogConfirm(title: string, description: string): Promise<boolean> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title,
      description,
      action: 'Delete',
      cancelText: 'Cancel',
      showType: 'warning',
    };
    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);
    return firstValueFrom(dialogRef.afterClosed());
  }

  async onDelete(item: Prayer) {
    const confirmed = await this.dialogConfirm(
      'Delete Prayer',
      `Are you sure you want to delete the prayer request "${item.subject}"?`
    );
    if (!confirmed) return;

    this.store.dispatch(
      prayerActions.deleteAction({
        id: item.id,
      }),
    );
  }

  async addMore() {
    const group: Group | null | undefined = await firstValueFrom(
      this.group$.pipe(first()),
    );
    const prayers: Prayer[] | null | undefined = await firstValueFrom(
      this.prayers$.pipe(first()),
    );

    if (prayers != undefined) {
      this.store.dispatch(
        prayerActions.getPrayers({
          groupId: this.groupId!,
          memberType:
            (group?.memberType as MemberType | undefined) ?? MemberType.member,
          lastRecord:
            prayers!.length > 0 ? prayers[prayers!.length - 1].modified : null,
        }),
      );
    }
  }
}
